import { useState, useEffect, useRef } from 'react';
import { useSpeechSynthesis } from 'context/speech-synthesis';

import * as Sentry from '@sentry/react';
import { useGeneralState } from 'context/general-state';

const reportAudioIssue = ({ audioUrl, fallbackText, errorMessage, state }) => {
  Sentry.captureMessage(`Issue with audio file | ${errorMessage}`, {
    extra: {
      state,
      message: errorMessage,
      audioUrl,
      fallbackText,
    },
    level: 'warning',
  });
};

const ensureAudioContext = async () => {
  if (typeof window !== 'undefined') {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (AudioContext) {
      const ctx = new AudioContext();
      if (ctx.state === 'suspended') {
        try {
          await ctx.resume();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('Failed to resume audio context:', error);
        }
      }
    }
  }
};

const isIOS = () => {
  const userAgent = navigator.userAgent;
  const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent);

  // Check for iPad Pro running iOS 13+ which might report as Mac
  const isMacLikeDevice =
    userAgent.includes('Mac') && navigator.maxTouchPoints > 1;

  return isIOSDevice || isMacLikeDevice;
};

const isSafari = () => {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

function useAudioPlayer(audioUrl, onEnd) {
  const [canPlay, setCanPlay] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [audioCheckDone, setAudioCheckDone] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const audioRef = useRef();

  useEffect(() => {
    if (!audioUrl) {
      setCanPlay(false);
      setAudioCheckDone(true);
      return;
    }

    audioRef.current = new Audio();
    const audio = audioRef.current;

    // iOS-specific settings
    if (isIOS()) {
      audio.preload = 'auto';
      audio.playsInline = true;
    }

    const handleCanPlayThrough = () => {
      setCanPlay(true);
      setAudioCheckDone(true);
    };

    const handleError = (e) => {
      setCanPlay(false);
      setAudioCheckDone(true);
      const mediaError = e.target?.error;

      let errorMsg = 'Unknown audio error';
      if (mediaError) {
        switch (mediaError.code) {
          case MediaError.MEDIA_ERR_ABORTED:
            errorMsg = 'Audio playback aborted';
            break;
          case MediaError.MEDIA_ERR_NETWORK:
            errorMsg = 'Network error loading audio';
            break;
          case MediaError.MEDIA_ERR_DECODE:
            errorMsg = 'Audio decode error';
            break;
          case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            errorMsg = 'Audio format not supported';
            break;
          default:
            errorMsg = mediaError.message || 'Unknown audio error';
        }
      }

      Sentry.captureException(new Error(`Audio playback error: ${errorMsg}`), {
        extra: {
          audioUrl,
          mediaError,
          mediaErrorCode: mediaError?.code,
          userAgent: navigator.userAgent,
          isIOS: isIOS(),
          isSafari: isSafari(),
        },
        level: 'error',
      });
      setErrorMessage(errorMsg);
    };

    const handleEnded = () => {
      setPlaying(false);
      if (typeof onEnd === 'function') {
        onEnd();
      }
    };

    const handleLoadStart = () => {
      setAudioCheckDone(false);
    };

    audio.addEventListener('canplaythrough', handleCanPlayThrough);
    audio.addEventListener('error', handleError);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadstart', handleLoadStart);

    // Set source and load
    audio.src = audioUrl;
    audio.load();

    return () => {
      audio.pause();
      audio.removeEventListener('canplaythrough', handleCanPlayThrough);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('loadstart', handleLoadStart);
      // Clear src to help with cleanup
      audio.src = '';
    };
  }, [audioUrl, onEnd]);

  return {
    canPlay,
    playing,
    setPlaying,
    audioRef,
    audioCheckDone,
    errorMessage,
  };
}

export default function UseAudio({
  audioUrl,
  handleClick,
  onEnd,
  fallbackText,
}) {
  const { playingAudioRef } = useGeneralState();
  const {
    canPlay,
    setPlaying,
    audioRef,
    audioCheckDone,
    errorMessage,
    playing,
  } = useAudioPlayer(audioUrl, onEnd);

  const { speak } = useSpeechSynthesis();

  const handleAudioClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();

    // Ensure audio context is resumed for iOS
    await ensureAudioContext();

    if (handleClick && typeof handleClick === 'function') {
      handleClick();
    }

    // Stop any currently playing audio
    if (
      playingAudioRef.current &&
      playingAudioRef.current !== audioRef.current
    ) {
      playingAudioRef.current.pause();
      playingAudioRef.current.currentTime = 0;
    }

    // Cancel any ongoing speech synthesis
    if (window.speechSynthesis && window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
    }

    if (!audioCheckDone) {
      // If audio isn't ready yet, wait a bit and try fallback
      setTimeout(() => {
        if (fallbackText) {
          speak(fallbackText, onEnd);
        } else {
          onEnd && onEnd();
        }
      }, 1000);
      return;
    }

    if (canPlay && audioRef.current) {
      // Set the new audio as the playing audio
      playingAudioRef.current = audioRef.current;

      try {
        // Reset audio to beginning
        audioRef.current.currentTime = 0;

        // For iOS, we need to ensure the audio is fully loaded
        if (
          isIOS() &&
          audioRef.current.readyState < HTMLMediaElement.HAVE_ENOUGH_DATA
        ) {
          audioRef.current.load();

          // Wait for it to be ready
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(
              () => reject(new Error('Audio load timeout')),
              3000
            );

            const onCanPlay = () => {
              clearTimeout(timeout);
              audioRef.current.removeEventListener('canplaythrough', onCanPlay);
              audioRef.current.removeEventListener('error', onError);
              resolve();
            };

            const onError = (e) => {
              clearTimeout(timeout);
              audioRef.current.removeEventListener('canplaythrough', onCanPlay);
              audioRef.current.removeEventListener('error', onError);
              reject(e);
            };

            audioRef.current.addEventListener('canplaythrough', onCanPlay);
            audioRef.current.addEventListener('error', onError);
          });
        }

        await audioRef.current.play();
        setPlaying(true);
      } catch (error) {
        setPlaying(false);
        playingAudioRef.current = null;

        if (error.name === 'NotAllowedError') {
          reportAudioIssue({
            audioUrl,
            fallbackText,
            errorMessage:
              'Audio playback not allowed - user interaction required',
            state: {
              canPlay,
              playing,
              audioCheckDone,
              isIOS: isIOS(),
              isSafari: isSafari(),
            },
          });

          if (fallbackText) {
            speak(fallbackText, onEnd);
          }
        } else if (error.name === 'NotSupportedError') {
          // eslint-disable-next-line no-console
          console.warn('Audio format not supported - using fallback');
          reportAudioIssue({
            audioUrl,
            fallbackText,
            errorMessage: 'Audio format not supported',
            state: {
              canPlay,
              playing,
              audioCheckDone,
              isIOS: isIOS(),
              isSafari: isSafari(),
            },
          });

          if (fallbackText) {
            speak(fallbackText, onEnd);
          }
        } else if (error.name === 'AbortError') {
          // eslint-disable-next-line no-console
          console.warn(
            'Audio play was interrupted, likely due to pause() or unmount'
          );
        } else {
          // eslint-disable-next-line no-console
          console.error('Audio playback failed:', error);

          Sentry.captureException(error, {
            extra: {
              audioUrl,
              errorMessage: error.message,
              errorName: error.name,
              userAgent: navigator.userAgent,
              isIOS: isIOS(),
              isSafari: isSafari(),
            },
            level: 'error',
          });

          // Fall back to speech synthesis
          if (fallbackText) {
            speak(fallbackText, onEnd);
          }
        }
      }
    } else {
      reportAudioIssue({
        audioUrl,
        fallbackText,
        errorMessage: errorMessage || 'Audio cannot play',
        state: {
          canPlay,
          playing,
          audioCheckDone,
          isLoading: !audioCheckDone,
          isError: !!errorMessage,
          fallbackDisabled: !canPlay && !fallbackText,
          isIOS: isIOS(),
          isSafari: isSafari(),
        },
      });

      if (fallbackText) {
        speak(fallbackText, onEnd);
      } else {
        onEnd && onEnd();
      }
    }
  };

  return {
    handleAudioClick,
    isError: !!errorMessage,
    isLoading: !audioCheckDone,
    fallbackDisabled: !canPlay && !fallbackText,
  };
}
